import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import axios from 'axios';
import 'reactflow/dist/style.css';
import Toolbar from './Toolbar';
import { askChatGPT } from './chatgptApi';
import './App.css'; // Add this line

const initialNodes = [];
const initialEdges = []; // No initial edges
const API_URL = process.env.REACT_APP_API_URL_APP;

function Flow() {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [history, setHistory] = useState([]);
  const reactFlowInstance = useReactFlow();
  const fileInputRef = useRef(null);
  const [initiated, setInitiated] = useState(false);
  const [chatVisible, setChatVisible] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatResponse, setChatResponse] = useState('');
  const [flows, setFlows] = useState([]);
  const [showFlowList, setShowFlowList] = useState(false);
  const [isSending, setIsSending] = useState(false); // Add this line

  useEffect(() => {
    // Set default flow data
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [setNodes, setEdges]);

  const fitView = useCallback(() => {
    setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.1 });
    }, 10);
  }, [reactFlowInstance]);

  useEffect(() => {
    if (nodes.length > 0 && !initiated) {
      fitView();
      setInitiated(true);
    }
  }, [nodes, initiated, fitView]);

  useEffect(() => {
    if (initiated) {
      fitView();
    }
  }, [nodes.length, initiated, fitView]);

  const pushToHistory = useCallback(() => {
    setHistory((hist) => [...hist, { nodes, edges }]);
  }, [nodes, edges]);

  const onConnect = useCallback(
    (params) => {
      pushToHistory();
      setEdges((eds) => addEdge(params, eds));
    },
    [setEdges, pushToHistory]
  );

  const addNode = useCallback(() => {
    pushToHistory();
    const newNode = {
      id: (nodes.length + 1).toString(),
      type: 'default',
      data: { label: `Node ${nodes.length + 1}` },
      position: {
        x: 10, // Position near the top left
        y: 60, // Position just below the "Add Node" button
      },
    };
    setNodes((nds) => nds.concat(newNode));
  }, [nodes, setNodes, pushToHistory]);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  const handleNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  const handleEdgeClick = useCallback((event, edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
  }, []);

  const deleteNode = (nodeId) => {
    pushToHistory();
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setSelectedNode(null);
  };

  const renameNode = (nodeId) => {
    const newName = prompt('Enter new name:');
    if (newName) {
      pushToHistory();
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, label: newName } } : n
        )
      );
    }
    setSelectedNode(null);
  };

  const removeEdge = (edgeId) => {
    pushToHistory();
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    setSelectedEdge(null);
  };

  const undo = useCallback(() => {
    setHistory((hist) => {
      if (hist.length === 0) return hist;
      const lastState = hist[hist.length - 1];
      setNodes(lastState.nodes);
      setEdges(lastState.edges);
      return hist.slice(0, -1);
    });
  }, [setNodes, setEdges]);

  const saveSession = useCallback(async () => {
    const flowName = prompt('Enter flow name:');
    if (!flowName) {
      alert('Flow name is required');
      return;
    }
    try {
      await axios.post(
        'api/flow',
        { name: flowName, nodes, edges }
      );
      alert('Session saved successfully');
    } catch (err) {
      console.error('Error saving session:', err);
      alert('Error saving session');
    }
  }, [nodes, edges]);

  const loadSession = useCallback((event) => {
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      try {
        const sessionData = JSON.parse(e.target.result);
        if (sessionData.nodes && sessionData.edges) {
          setNodes(sessionData.nodes);
          setEdges(sessionData.edges);
        } else {
          alert('Invalid session file');
        }
      } catch (err) {
        console.error('Error parsing session file:', err);
        alert('Error parsing session file');
      }
    };
    if (event.target.files[0]) {
      fileReader.readAsText(event.target.files[0]);
    }
  }, [setNodes, setEdges]);

  const clearAll = useCallback(() => {
    pushToHistory();
    setNodes(initialNodes);
    setEdges(initialEdges);
    fitView();
  }, [setNodes, setEdges, fitView, pushToHistory]);

  const handleChatButtonClick = () => {
    setChatVisible(true);
  };

  const handleChatInputChange = (event) => {
    setChatInput(event.target.value);
  };

  const handleChatKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleChatSubmit();
    }
  };

  const handleChatSubmit = async () => {
    setIsSending(true); // Add this line
    const flowData = {
      nodes,
      edges
    };
    const jsonFlowData = JSON.stringify(flowData);
    const response = await askChatGPT(`${jsonFlowData}\n\n\n --> Based on this React Flow JSON, answer following question. Make sure to not mention the JSON In your response. The question is: ${chatInput}`); // Modify this line
    setChatResponse(response);
    setIsSending(false); // Add this line
  };

  const fetchFlows = useCallback(async () => {
    try {
      const response = await axios.get('api/flow');
      setFlows(response.data);
    } catch (err) {
      console.error('Error fetching flows:', err);
      alert('Error fetching flows');
    }
  }, []);

  const openFlow = useCallback((flow) => {
    setNodes(flow.nodes);
    setEdges(flow.edges);
    setShowFlowList(false);
  }, [setNodes, setEdges]);

  const handleOpenFlowClick = () => {
    fetchFlows();
    setShowFlowList(true);
  };

  const handleCloseChat = () => {
    setChatVisible(false);
  };

  return (
    <div className="app-container">
      <Toolbar
        addNode={addNode}
        selectedNode={selectedNode}
        renameNode={renameNode}
        deleteNode={deleteNode}
        selectedEdge={selectedEdge}
        removeEdge={removeEdge}
        saveSession={saveSession}
        fileInputRef={fileInputRef}
        loadSession={loadSession}
        clearAll={clearAll}
        undo={undo}
        history={history}
        handleOpenFlowClick={handleOpenFlowClick}
        handleChatButtonClick={handleChatButtonClick}
        isSending={isSending}
      />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onPaneClick={onPaneClick}
        onNodeClick={handleNodeClick}
        onEdgeClick={handleEdgeClick}
        connectionLineType="smoothstep"
        onInit={fitView}
      >
        <MiniMap className="minimap" />
        <Controls className="controls" />
        <Background className="background" />
      </ReactFlow>
      {chatVisible && (
        <div className="chat-container">
          <button className="close-button" onClick={handleCloseChat}>X</button>
          <textarea
            value={chatInput}
            onChange={handleChatInputChange}
            onKeyPress={handleChatKeyPress}
            rows="4"
            className="chat-input"
          />
          {!isSending && (
            <button className="chat-submit-button" onClick={handleChatSubmit}>
              Send
            </button>
          )}
          {chatResponse && (
            <div className="chat-response">
              {chatResponse}
            </div>
          )}
        </div>
      )}
      {showFlowList && (
        <div className="flow-list-container">
          <h3>Select a Flow</h3>
          <ul>
            {flows.map((flow, index) => (
              <li key={index} onClick={() => openFlow(flow)}>
                {flow.name}
              </li>
            ))}
          </ul>
          <button onClick={() => setShowFlowList(false)}>Close</button>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <ReactFlowProvider>
      <Flow />
    </ReactFlowProvider>
  );
}

export default App;